import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { sanitizePayload } from "./sanitize";
import { validateAppLogSource, validateAppLogLevel } from "@/types/app-logs";

export type LogLevel = "DEBUG" | "INFO" | "WARN" | "ERROR" | "CRITICAL";
export type LogChannel = "log" | "event" | "error";
export type ErrorType = "ui" | "network" | "auth" | "security" | "unexpected";
export type IncidentImpact = "single_user" | "multiple_users" | "global";
export type SuggestedAction = "reload" | "relogin" | "disable_feature";

export interface TelemetryContext {
  screen_id?: string;
  screen_name?: string;
  route?: string;
  action?: string;
  component?: string;
  parent_action?: string;
  feature?: string;
  function_name?: string;
  user_role?: string;
  session_id?: string;
  trace_id?: string;
  request_id?: string;
  user_id?: string;
  anonymous_id?: string;
  feature_flags?: Record<string, string>;
  is_test_user?: boolean;
}

export interface TelemetryOptions {
  context?: TelemetryContext;
  data?: Record<string, unknown>;
  allowList?: string[];
  error?: unknown;
  tags?: Record<string, string>;
  support_message_key?: string;
  error_code?: string;
  error_type?: ErrorType;
  impact?: IncidentImpact;
  suggested_action?: SuggestedAction;
}

const SESSION_STORAGE_KEY = "drug-logger-session-id";
const ANONYMOUS_STORAGE_KEY = "drug-logger-anonymous-id";
const LOG_SCHEMA_VERSION = "1.0";
const CRITICAL_RATE_LIMIT_MS = 60_000;
const APP_START_MS = typeof performance !== "undefined" ? performance.now() : 0;

const criticalRateLimit = new Map<string, number>();

function createId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (error) {
    console.warn("[logger] Failed to generate crypto uuid", error);
  }
  return Math.random().toString(16).slice(2, 10);
}

function getSessionId(): string {
  if (typeof window === "undefined") return createId();
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const generated = createId();
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
    return generated;
  } catch {
    return createId();
  }
}

function getAnonymousId(): string {
  if (typeof window === "undefined") return createId();
  try {
    const existing = window.localStorage.getItem(ANONYMOUS_STORAGE_KEY);
    if (existing) return existing;
    const generated = createId();
    window.localStorage.setItem(ANONYMOUS_STORAGE_KEY, generated);
    return generated;
  } catch {
    return createId();
  }
}

export function createTraceId(): string {
  return createId();
}

export function createRequestId(): string {
  return createId();
}

function getEnvironment(): string {
  // VITE_APP_ENV настроен для каждого окружения (dev/stage/prod)
  // import.meta.env.MODE возвращает 'production' для любого build
  const appEnv = import.meta.env.VITE_APP_ENV;
  if (appEnv) return appEnv;
  
  return import.meta.env.MODE || "unknown";
}

function getAppVersion(): string {
  return import.meta.env.VITE_APP_VERSION || "unknown";
}

function getPlatform(): "webview" | "web" {
  if (typeof navigator === "undefined") return "web";
  const ua = navigator.userAgent || "";
  if (ua.includes("wv") || ua.includes("WebView") || (window as Window & { ReactNativeWebView?: unknown }).ReactNativeWebView) {
    return "webview";
  }
  return "web";
}

function getWebviewVersion(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (ua.includes("wv")) return "android-webview";
  return "unknown";
}

function getNetworkState(): "online" | "offline" | "slow" {
  if (typeof navigator === "undefined") return "online";
  if (navigator.onLine === false) return "offline";
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;
  const effectiveType = connection?.effectiveType || "";
  if (effectiveType.includes("2g") || effectiveType.includes("slow")) return "slow";
  return "online";
}

function getTimeSinceStart(): number {
  if (typeof performance === "undefined") return 0;
  return Math.max(0, Math.round(performance.now() - APP_START_MS));
}

function isColdStart(timeSinceStart: number): boolean {
  return timeSinceStart < 30_000;
}

function pickAllowedData(data: Record<string, unknown> | undefined, allowList?: string[]): Record<string, unknown> | undefined {
  if (!data || !allowList || allowList.length === 0) return undefined;
  const picked: Record<string, unknown> = {};
  allowList.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      picked[key] = data[key];
    }
  });
  return sanitizePayload(picked);
}

function serializeError(error: unknown): { error_name?: string; error_message?: string; stack?: string } {
  if (error instanceof Error) {
    return {
      error_name: error.name,
      error_message: error.message,
      stack: error.stack,
    };
  }
  if (typeof error === "string") {
    return { error_message: error };
  }
  return {};
}

let baseContext: TelemetryContext = {
  session_id: getSessionId(),
  anonymous_id: getAnonymousId(),
};

let activeTraceId: string = createTraceId();
let pendingRequestContext: { trace_id: string; request_id: string } | null = null;

function ensureTraceId(provided?: string): string {
  if (provided) return provided;
  if (!activeTraceId) {
    activeTraceId = createTraceId();
  }
  return activeTraceId;
}

function mergeContext(context?: TelemetryContext): TelemetryContext {
  return { ...baseContext, ...context };
}

function buildPayload(
  level: LogLevel,
  channel: LogChannel,
  message: string,
  options?: TelemetryOptions
): Record<string, unknown> {
  const mergedContext = mergeContext(options?.context);
  const timeSinceStart = getTimeSinceStart();
  const traceId = ensureTraceId(mergedContext.trace_id);
  const requestId = mergedContext.request_id;
  const errorMeta = options?.error ? serializeError(options.error) : {};

  const payload = {
    timestamp: new Date().toISOString(),
    environment: getEnvironment(),
    app_version: getAppVersion(),
    platform: getPlatform(),
    log_schema_version: LOG_SCHEMA_VERSION,
    log_level: level,
    channel,
    message,
    user_id: mergedContext.user_id,
    anonymous_id: mergedContext.anonymous_id,
    role: mergedContext.user_role,
    session_id: mergedContext.session_id,
    screen_id: mergedContext.screen_id,
    screen_name: mergedContext.screen_name,
    route: mergedContext.route,
    action: mergedContext.action,
    component: mergedContext.component,
    parent_action: mergedContext.parent_action,
    feature: mergedContext.feature,
    function_name: mergedContext.function_name,
    trace_id: traceId,
    request_id: requestId,
    feature_flags: mergedContext.feature_flags,
    network_state: getNetworkState(),
    webview_version: getWebviewVersion(),
    time_since_app_start_ms: timeSinceStart,
    cold_start: isColdStart(timeSinceStart),
    is_test_user: mergedContext.is_test_user,
    is_e2e_run: Boolean((window as Window & { Cypress?: unknown }).Cypress || (window as Window & { __E2E__?: unknown }).__E2E__),
    support_message_key: options?.support_message_key,
    error_code: options?.error_code ?? (channel === "error" ? "unknown_error" : undefined),
    error_type: options?.error_type ?? (channel === "error" ? "unexpected" : undefined),
    impact: options?.impact,
    suggested_action: options?.suggested_action,
    data: pickAllowedData(options?.data, options?.allowList),
    ...errorMeta,
  };

  return sanitizePayload(payload);
}

function toSentryLevel(level: LogLevel): Sentry.SeverityLevel {
  switch (level) {
    case "WARN":
      return "warning";
    case "ERROR":
    case "CRITICAL":
      return "error";
    default:
      return "info";
  }
}

function shouldRateLimitCritical(errorCode?: string): boolean {
  if (!errorCode) return false;
  const now = Date.now();
  const last = criticalRateLimit.get(errorCode);
  if (last && now - last < CRITICAL_RATE_LIMIT_MS) {
    return true;
  }
  criticalRateLimit.set(errorCode, now);
  return false;
}

function sendToSentry(level: LogLevel, payload: Record<string, unknown>, error?: unknown, tags?: Record<string, string>) {
  if (level !== "ERROR" && level !== "CRITICAL") return;
  const exception = error instanceof Error ? error : new Error(payload.message ? String(payload.message) : "Unknown error");
  Sentry.captureException(exception, (scope) => {
    scope.setLevel(level === "CRITICAL" ? "fatal" : "error");
    scope.setContext("log_payload", payload);
    Object.entries(tags ?? {}).forEach(([key, value]) => scope.setTag(key, value));
    return scope;
  });
}

function addBreadcrumb(level: LogLevel, message: string, payload: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    level: toSentryLevel(level),
    message,
    data: payload,
    category: (payload.feature as string) || "app",
  });
}

const DB_LOG_RATE_LIMIT_MS = 1_500;
const dbLogRateLimit = new Map<string, number>();

function shouldWriteToAppLogs(level: LogLevel): boolean {
  // Keep DB logs high-signal to avoid noise/cost.
  return level === "WARN" || level === "ERROR" || level === "CRITICAL";
}

function buildAppLogsContext(payload: Record<string, unknown>): Json {
  // IMPORTANT: avoid storing personal identifiers; sanitizePayload already masks PII.
  const ctx = {
    trace_id: payload.trace_id,
    route: payload.route,
    feature: payload.feature,
    function_name: payload.function_name,
    screen_id: payload.screen_id,
    channel: payload.channel,
    data: payload.data,
    error_code: payload.error_code,
    error_type: payload.error_type,
    error_name: payload.error_name,
    error_message: payload.error_message,
  };

  return sanitizePayload(ctx as Record<string, unknown>) as unknown as Json;
}

async function writeToAppLogs(level: LogLevel, message: string, payload: Record<string, unknown>) {
  try {
    if (!shouldWriteToAppLogs(level)) return;

    const key = `${level}:${message}`;
    const now = Date.now();
    const last = dbLogRateLimit.get(key);
    if (last && now - last < DB_LOG_RATE_LIMIT_MS) return;
    dbLogRateLimit.set(key, now);

    const { error } = await supabase.from("app_logs").insert([
      {
        event: String(message),
        level: validateAppLogLevel(level),
        source: validateAppLogSource("client"),
        // DB now has default gen_random_uuid() for request_id, so we can omit it
        screen_id: payload.screen_id ? String(payload.screen_id) : null,
        rpc_name: null,
        context: buildAppLogsContext(payload),
      },
    ]);

    if (error) {
      // Avoid recursion into logger
      console.warn("[logger] Failed to write app_logs", { code: (error as any)?.code, message: (error as any)?.message });
    }
  } catch (e) {
    console.warn("[logger] Failed to write app_logs (unexpected)", e);
  }
}

/**
 * Sanitize URL for logging: removes hash and sensitive query params
 * to prevent leaking tokens/PII in logs
 */
function sanitizeUrlForLogs(inputUrl: string): string {
  try {
    const url = new URL(inputUrl);
    // Remove hash entirely (may contain access_token, refresh_token, etc.)
    url.hash = '';
    
    // Remove sensitive query params
    const sensitiveParams = [
      'access_token', 'refresh_token', 'token', 'code', 'password',
      'email', 'state', 'error_description', 'redirect_to', 'token_hash',
    ];
    sensitiveParams.forEach((param) => url.searchParams.delete(param));
    
    return url.toString();
  } catch {
    // If URL parsing fails, return origin + pathname only
    if (typeof window !== 'undefined') {
      return window.location.origin + window.location.pathname;
    }
    return '[invalid-url]';
  }
}

// Send critical errors to Telegram via edge function
async function sendCriticalToTelegram(
  message: string, 
  options: TelemetryOptions & { error_code: string; impact: IncidentImpact; suggested_action: SuggestedAction }
) {
  try {
    const errorCode = `CRIT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 4).toUpperCase()}`;
    const errorMessage = options.error instanceof Error 
      ? options.error.message 
      : (typeof options.error === 'string' ? options.error : message);
    const errorStack = options.error instanceof Error ? options.error.stack : undefined;
    
    const { error } = await supabase.functions.invoke('error-report', {
      body: {
        type: 'critical',
        errorCode,
        errorMessage,
        errorStack: errorStack?.substring(0, 500),
        timestamp: new Date().toISOString(),
        userId: baseContext.user_id,
        sessionId: baseContext.session_id,
        environment: getEnvironment(),
        appVersion: getAppVersion(),
        level: 'CRITICAL',
        feature: options.context?.feature,
        screenId: options.context?.screen_id,
        traceId: options.context?.trace_id,
        impact: options.impact,
        suggestedAction: options.suggested_action,
        url: typeof window !== 'undefined' ? sanitizeUrlForLogs(window.location.href) : undefined,
      },
    });
    
    if (error) {
      console.warn('[logger] Failed to send critical to Telegram:', error);
    }
  } catch (e) {
    // Don't let Telegram failures affect the app
    console.warn('[logger] Failed to send critical to Telegram (unexpected):', e);
  }
}

// Send unhandled errors to Telegram
async function sendUnhandledToTelegram(
  errorMessage: string,
  errorStack: string | undefined,
  errorType: 'error' | 'unhandledrejection'
) {
  try {
    const errorCode = `UNH-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 4).toUpperCase()}`;
    
    const { error } = await supabase.functions.invoke('error-report', {
      body: {
        type: 'unhandled',
        errorCode,
        errorMessage,
        errorStack: errorStack?.substring(0, 600),
        errorType,
        timestamp: new Date().toISOString(),
        userId: baseContext.user_id,
        sessionId: baseContext.session_id,
        environment: getEnvironment(),
        appVersion: getAppVersion(),
        url: typeof window !== 'undefined' ? sanitizeUrlForLogs(window.location.href) : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      },
    });
    
    if (error) {
      console.warn('[logger] Failed to send unhandled error to Telegram:', error);
    }
  } catch (e) {
    console.warn('[logger] Failed to send unhandled error to Telegram (unexpected):', e);
  }
}

// Rate limit for unhandled errors
const unhandledRateLimit = new Map<string, number>();
const UNHANDLED_RATE_LIMIT_MS = 30_000;

function shouldRateLimitUnhandled(message: string): boolean {
  // Защита от non-string значений (причина ошибки UNH-MKSS27ZP-PN)
  if (typeof message !== 'string' || !message) {
    return false; // Не rate-limit неизвестные ошибки
  }
  const key = message.substring(0, 100);
  const now = Date.now();
  const last = unhandledRateLimit.get(key);
  if (last && now - last < UNHANDLED_RATE_LIMIT_MS) {
    return true;
  }
  unhandledRateLimit.set(key, now);
  return false;
}

// Setup global error handlers
function setupGlobalErrorHandlers() {
  if (typeof window === 'undefined') return;
  
  // Handle uncaught errors (both JS errors and resource load errors)
  window.addEventListener('error', (event) => {
    // Distinguish between JS errors and resource load errors
    const target = event.target as HTMLElement | null;
    const isResourceError = target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK' || target.tagName === 'IMG');
    
    if (isResourceError) {
      const tagName = target.tagName.toLowerCase();
      
      // Изображения — не критичны, обрабатываются на уровне компонентов (onError)
      // Не отправляем в Telegram, только debug-лог в dev
      if (tagName === 'img') {
        if (getEnvironment() !== 'production') {
          const src = (target as HTMLImageElement).src || 'unknown';
          console.debug('[logger] Image failed to load:', src.split('/').pop());
        }
        return;
      }
      
      // Скрипты и стили — потенциально критичны, отправляем в Telegram
      const src = (target as HTMLScriptElement).src || (target as HTMLLinkElement).href || 'unknown';
      const sanitizedSrc = sanitizeUrlForLogs(src);
      const message = `Resource failed to load: ${tagName} src=${sanitizedSrc}`;
      
      if (shouldRateLimitUnhandled(message)) return;
      
      console.warn('[logger] Resource load error:', message);
      void sendUnhandledToTelegram(message, undefined, 'error');
      return;
    }
    
    // Regular JS error
    const message = event.error?.message || event.message;
    
    // Ignore cross-origin script errors (CORS policy hides details)
    // - Empty message: no error info available
    // - "Script error.": generic cross-origin error message (browser masks details)
    // This happens when 3rd-party scripts (Supabase SDK, etc.) throw errors
    const isCrossOriginError = !message || message === 'Script error.' || message === 'Script error';
    
    if (isCrossOriginError) {
      if (getEnvironment() !== 'production') {
        console.debug('[logger] Cross-origin error (ignored):', {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
      }
      return;
    }
    
    const stack = event.error?.stack;
    
    if (shouldRateLimitUnhandled(message)) return;
    
    console.error('[logger] Unhandled error:', message);
    void sendUnhandledToTelegram(message, stack, 'error');
  }, true); // Use capture phase to catch resource errors
  
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    
    // Robust message extraction (фикс ошибки UNH-MKSS27ZP-PN)
    let message: string;
    if (reason instanceof Error) {
      message = reason.message || reason.name || 'Unknown Error';
    } else if (typeof reason === 'string') {
      message = reason;
    } else {
      message = String(reason ?? 'Unknown rejection');
    }
    
    const stack = reason instanceof Error ? reason.stack : undefined;
    
    if (shouldRateLimitUnhandled(message)) return;
    
    console.error('[logger] Unhandled promise rejection:', message);
    void sendUnhandledToTelegram(message, stack, 'unhandledrejection');
  });
}

// Initialize global handlers
setupGlobalErrorHandlers();

function emit(level: LogLevel, channel: LogChannel, message: string, options?: TelemetryOptions) {
  if (level === "DEBUG" && getEnvironment() === "production") return;
  const payload = buildPayload(level, channel, message, options);
  const output = `[DRUG] ${JSON.stringify(payload)}`;

  switch (level) {
    case "WARN":
      console.warn(output);
      break;
    case "ERROR":
    case "CRITICAL":
      console.error(output);
      break;
    default:
      console.info(output);
  }

  addBreadcrumb(level, message, payload);
  sendToSentry(level, payload, options?.error, options?.tags);
  void writeToAppLogs(level, message, payload);
}

export function setBaseLogContext(context: Partial<TelemetryContext>) {
  baseContext = { ...baseContext, ...context };
  Sentry.setContext("logger", sanitizePayload(baseContext as Record<string, unknown>));
}

export function setUserLogContext(user?: { id?: string; role?: string | null; sessionId?: string; isTestUser?: boolean }) {
  baseContext = {
    ...baseContext,
    user_id: user?.id ?? baseContext.user_id,
    user_role: user?.role ?? baseContext.user_role,
    session_id: user?.sessionId ?? baseContext.session_id,
    is_test_user: user?.isTestUser ?? baseContext.is_test_user,
  };

  if (user?.id) {
    Sentry.setUser({ id: user.id });
  } else {
    Sentry.setUser(null);
  }

  if (user?.role) {
    Sentry.setTag("user_role", user.role);
  }
}

export function startTrace(params: { action: string; context?: TelemetryContext; parent_action?: string }): string {
  const traceId = createTraceId();
  activeTraceId = traceId;
  const traceContext: TelemetryContext = {
    ...params.context,
    action: params.action,
    parent_action: params.parent_action,
    trace_id: traceId,
  };
  emit("INFO", "event", "trace_start", { context: traceContext, allowList: [] });
  return traceId;
}

export function endTrace(traceId: string, status: "success" | "failure" = "success"): void {
  emit("INFO", "event", "trace_end", {
    context: { trace_id: traceId, action: "trace_end" },
    data: { status },
    allowList: ["status"],
  });
  if (activeTraceId === traceId) {
    activeTraceId = createTraceId();
  }
}

export function createRequestContext(
  context?: TelemetryContext,
  options?: { queue?: boolean }
): { trace_id: string; request_id: string; headers: Record<string, string> } {
  const mergedContext = mergeContext(context);
  const trace_id = ensureTraceId(mergedContext.trace_id);
  const request_id = mergedContext.request_id ?? createRequestId();

  const headers: Record<string, string> = {
    "x-trace-id": trace_id,
    "x-request-id": request_id,
    "x-session-id": mergedContext.session_id || getSessionId(),
    "x-anonymous-id": mergedContext.anonymous_id || getAnonymousId(),
    "x-app-version": getAppVersion(),
  };

  if (mergedContext.user_id) {
    headers["x-user-id"] = mergedContext.user_id;
  }
  if (mergedContext.user_role) {
    headers["x-user-role"] = mergedContext.user_role;
  }

  activeTraceId = trace_id;
  if (options?.queue !== false) {
    pendingRequestContext = { trace_id, request_id };
  }

  return { trace_id, request_id, headers };
}

export function consumeRequestContext(): { trace_id: string; request_id: string } | null {
  if (!pendingRequestContext) return null;
  const next = pendingRequestContext;
  pendingRequestContext = null;
  return next;
}

export const logger = {
  debug: (message: string, options?: TelemetryOptions) => emit("DEBUG", "log", message, options),
  info: (message: string, options?: TelemetryOptions) => emit("INFO", "log", message, options),
  warn: (message: string, options?: TelemetryOptions) => emit("WARN", "log", message, options),
  error: (message: string, options?: TelemetryOptions) => emit("ERROR", "error", message, options),
  critical: (message: string, options: TelemetryOptions & { error_code: string; impact: IncidentImpact; suggested_action: SuggestedAction }) => {
    if (shouldRateLimitCritical(options.error_code)) return;
    emit("CRITICAL", "log", message, options);
    emit("CRITICAL", "event", `${message}_critical_event`, options);
    sendToSentry("CRITICAL", buildPayload("CRITICAL", "error", message, options), options.error, options.tags);
    // Send critical error to Telegram
    void sendCriticalToTelegram(message, options);
  },
  event: (eventName: string, options?: TelemetryOptions) => emit("INFO", "event", eventName, options),
  createTraceId,
  createRequestId,
  createRequestContext,
  setBaseLogContext,
  setUserLogContext,
  startTrace,
  endTrace,
  getSessionId,
};

export type Logger = typeof logger;
